import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  DeleteForever as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import InitiativeChangelog from "./InitiativeChangelog";
import InitiativeComments from "./InitiativeComments";
import InitiativeDetailFields from "./InitiativeDetailFields";
import ConfirmDialog from "./ConfirmDialog";
import LookupChip from "./LookupChip";
import { useLookupColorMaps } from "../hooks/useLookups";
import { useFrozenValue } from "../hooks/useFrozenValue";

export default function InitiativeDialog({ selection }) {
  const {
    selected: initiative,
    close: onClose,
    editSelected: onEdit,
    archiveToggleSelected: onArchiveToggle,
    deleteSelected: onDelete,
    addCommentSelected: onAddComment,
    editCommentSelected: onEditComment,
    deleteCommentSelected: onDeleteComment,
    currentUserId,
    isAdmin,
  } = selection;
  const displayed = useFrozenValue(initiative, Boolean(initiative));
  const { horizonColors, statusColors, priorityColors } = useLookupColorMaps();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!initiative) setIsConfirmingDelete(false);
  }, [initiative]);

  const styles = {
    metaRow: {
      display: "flex",
      flexDirection: "row",
      gap: 1,
      flexWrap: "wrap",
      mb: 2,
    },
    title: {
      color: "background.text",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    titleText: {
      flex: 1,
      minWidth: 0,
      overflowWrap: "anywhere",
    },
    titleActions: { flexShrink: 0 },
    background: { bgcolor: "background.dialog", color: "background.text" },
    closeBtn: { m: 1, textTransform: "none" },
    editIcon: { alignSelf: "center" },
  };

  return (
    <>
      <Dialog
        open={Boolean(initiative)}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        slotProps={{ paper: { sx: styles.background } }}
      >
        <DialogTitle sx={styles.title}>
          <Box component="span" sx={styles.titleText}>
            {displayed?.title}
          </Box>
          <Stack direction="row" sx={styles.titleActions}>
            {onArchiveToggle && (
              <Tooltip title={displayed?.archived ? "Unarchive" : "Archive"}>
                <IconButton
                  size="small"
                  color="archived"
                  onClick={onArchiveToggle}
                >
                  {displayed?.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setIsConfirmingDelete(true)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={styles.metaRow}>
            <LookupChip
              label={displayed?.horizon}
              hex={horizonColors[displayed?.horizon]}
              variant="outlined"
            />
            <LookupChip
              label={displayed?.status}
              hex={statusColors[displayed?.status]}
            />
            <LookupChip
              label={displayed?.priority}
              hex={priorityColors[displayed?.priority]}
              variant="outlined"
            />
            {displayed?.archived && (
              <Chip label="Archived" size="small" color="archived" />
            )}
          </Box>

          <InitiativeDetailFields initiative={displayed} />

          <InitiativeChangelog initiative={displayed} />

          <InitiativeComments
            key={displayed?.id}
            comments={displayed?.comments}
            actions={{
              onAdd: onAddComment,
              onEdit: onEditComment,
              onDelete: onDeleteComment,
            }}
            viewer={{ id: currentUserId, isAdmin }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={styles.closeBtn}>
            Close
          </Button>
          {onEdit && (
            <Button
              variant="contained"
              startIcon={<EditIcon sx={styles.editIcon} />}
              onClick={onEdit}
              sx={styles.closeBtn}
            >
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        onConfirm={onDelete}
        title="Delete this initiative?"
        message={`Unlike archiving, deleting "${displayed?.title}" is permanent and cannot be undone.`}
      />
    </>
  );
}
